/**
 * 雛形のサブステップの「計画を作る時刻」(spec AC-WFT-048 / AC-WFT-049) の
 * 表示条件・入力の解釈・wire への投影。
 *
 * `holdsPlanCreationTime` は backend の `validatePlanCreationTimeHolder`
 * (go/internal/backend/domain/entity/workflow/sub_step_template.go) のミラー。
 * backend 側の条件を変えた時は本ファイルも同期する (planAutoSubmitOffset.ts と同じ扱い)。
 *
 * 値は日数と時刻の 2 欄で編集するが、1 つの値として解決する。form state は 2 field:
 * - `planCreationTime`: Get で読み込んだ値 (UI からは書かない)
 * - `planCreationTimeInput`: 欄に打った生の文字列の組 (undefined = 未編集)。
 *   片方の欄を打つ時、もう片方は表示中の文字列を入れた組で書く
 *
 * feature flag は引数に取らない。flag は欄を出すかどうか
 * (`isPlanCreationTimeFieldShown`。編集済みの行は flag に関わらず出す) だけに使い、検証と送信は kind / 入力文字列だけで
 * 決める。flag の再取得中に入力値が黙って読み込み値に戻るのを避けるため。
 */
import { LinkedPageKind } from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'

export const PLAN_CREATION_TIME_MAX_DAYS = 30

export type PlanCreationTimeValue = {
  daysBefore: number
  localTime: string
}

export type PlanCreationTimeInput = { days: string; time: string }

/** 欄に表示する文字列 (invalid / cleared / entered は打った生の文字列)。 */
type Text = { days: string; time: string }

export type PlanCreationTimeResolution =
  /** kind が holder でない。読み込み値をそのまま送る (消去は backend の discard)。 */
  | { kind: 'notHolder'; wire: PlanCreationTimeValue | undefined }
  /** holder・未編集。未設定は未設定のまま送り、欄は空にする。 */
  | ({ kind: 'untouched'; wire: PlanCreationTimeValue | undefined } & Text)
  /** holder・両欄とも空。未設定にする。 */
  | ({ kind: 'cleared' } & Text)
  /** holder・編集済み・妥当。 */
  | ({ kind: 'entered'; value: PlanCreationTimeValue } & Text)
  /** holder・編集済み・不正 (片方だけの入力を含む)。 */
  | ({ kind: 'invalid' } & Text)

export type PlanCreationTimeRow = {
  linkedPageKind?: LinkedPageKind | undefined
  planCreationTime?: PlanCreationTimeValue | undefined
  planCreationTimeInput?: PlanCreationTimeInput | undefined
}

export function holdsPlanCreationTime(
  kind: LinkedPageKind | undefined,
): boolean {
  return (
    kind === LinkedPageKind.PLAN_PROCUREMENT ||
    kind === LinkedPageKind.PLAN_GEN_SALES ||
    kind === LinkedPageKind.PLAN_DEMAND_SUPPRESSION ||
    kind === LinkedPageKind.PLAN_SPLIT_SUPPLY
  )
}

const LOCAL_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/

/** trim 後に 0 以上の整数表記で 30 以下のものだけ受理する。 */
function parseDays(text: string): number | undefined {
  const trimmed = text.trim()
  if (!/^\d+$/.test(trimmed)) return undefined
  const n = Number(trimmed)
  return n > PLAN_CREATION_TIME_MAX_DAYS ? undefined : n
}

function parseLocalTime(text: string): string | undefined {
  const trimmed = text.trim()
  return LOCAL_TIME_PATTERN.test(trimmed) ? trimmed : undefined
}

export function resolvePlanCreationTime(
  row: PlanCreationTimeRow,
): PlanCreationTimeResolution {
  const loaded = row.planCreationTime
  if (!holdsPlanCreationTime(row.linkedPageKind)) {
    return { kind: 'notHolder', wire: loaded }
  }
  const input = row.planCreationTimeInput
  if (input === undefined) {
    return {
      kind: 'untouched',
      days: loaded === undefined ? '' : String(loaded.daysBefore),
      time: loaded?.localTime ?? '',
      wire: loaded,
    }
  }
  const text = { days: input.days, time: input.time }
  if (input.days.trim() === '' && input.time.trim() === '') {
    return { kind: 'cleared', ...text }
  }
  const daysBefore = parseDays(input.days)
  const localTime = parseLocalTime(input.time)
  return daysBefore === undefined || localTime === undefined
    ? { kind: 'invalid', ...text }
    : { kind: 'entered', ...text, value: { daysBefore, localTime } }
}

/** Update / Create request に載せる値。不正値は決して送らない。 */
export function planCreationTimeForWire(
  row: PlanCreationTimeRow,
): PlanCreationTimeValue | undefined {
  const resolution = resolvePlanCreationTime(row)
  switch (resolution.kind) {
    case 'notHolder':
    case 'untouched':
      return resolution.wire
    case 'cleared':
      return undefined
    case 'entered':
      return resolution.value
    case 'invalid':
      return row.planCreationTime
  }
}

export function isPlanCreationTimeFieldShown(
  resolution: PlanCreationTimeResolution,
  automationAvailable: boolean,
): boolean {
  if (resolution.kind === 'notHolder') return false
  // 利用者が欄を編集済み (untouched 以外) の行は、flag の再取得が失敗・OFF に
  // 転じても欄を残す。消すと AC-WFT-049 の文言が当該行に出ず、直す欄も無くなる。
  return automationAvailable || resolution.kind !== 'untouched'
}
